/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRolesRequest,
  ListRolesSecurity,
} from "../models/operations/listroles.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRolesQuery,
  prefetchRoles,
  queryKeyRoles,
  RolesQueryData,
} from "./roles.core.js";
export { buildRolesQuery, prefetchRoles, queryKeyRoles, type RolesQueryData };

export type RolesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRoles access
 *
 * @remarks
 * List all roles for the current organization.
 */
export function useRoles(
  request?: ListRolesRequest | undefined,
  security?: ListRolesSecurity | undefined,
  options?: QueryHookOptions<RolesQueryData, RolesQueryError>,
): UseQueryResult<RolesQueryData, RolesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRolesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRoles access
 *
 * @remarks
 * List all roles for the current organization.
 */
export function useRolesSuspense(
  request?: ListRolesRequest | undefined,
  security?: ListRolesSecurity | undefined,
  options?: SuspenseQueryHookOptions<RolesQueryData, RolesQueryError>,
): UseSuspenseQueryResult<RolesQueryData, RolesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRolesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRolesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: RolesQueryData,
): RolesQueryData | undefined {
  const key = queryKeyRoles(...queryKeyBase);

  return client.setQueryData<RolesQueryData>(key, data);
}

export function invalidateRoles(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listRoles", ...queryKeyBase],
  });
}

export function invalidateAllRoles(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listRoles"],
  });
}
